import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useAuthStore } from '@/core/stores/auth.store';
import { membersService } from '../api/members.service';
import { MEMBERS_KEYS } from './queryKeys';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import type { Member } from '../types';

export interface UseMembersResult {
  data: Member[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
}

/**
 * Fetches the paginated member list for the active organization.
 *
 * @param search - Optional search term sent as `SearchTerm` to the API
 * @param pageIndex - Zero-based page index (converted to 1-based internally)
 * @param pageSize - Number of items per page
 *
 * @remarks
 * `isCurrentUser` is computed by comparing each member's `userId` against
 * the authenticated user's `id` from the auth store.
 * The query is disabled when `activeOrganizationId` is not set.
 */
export function useMembers(
  search: string,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  sortBy?: string,
  sortAscending?: boolean,
): UseMembersResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const currentUserId = useAuthStore((s) => s.user?.id);

  const params = {
    SearchTerm: search || undefined,
    Page: pageIndex + 1, // API is 1-based
    PageSize: pageSize,
    SortBy: sortBy,
    SortAscending: sortAscending,
  };

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: MEMBERS_KEYS.list(orgId, params),
    queryFn: ({ signal }) => membersService.getAll(params, currentUserId, signal),
    staleTime: 60_000,
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });

  return {
    data: data?.items ?? [],
    totalCount: data?.total ?? 0,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
}
